import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import logo from './logo.png';
import FreeDropdown from './FreeDropdown';
import VideoPopup from './VideoPopup';
import DiveSessionHistoryOverlay from './DiveSessionHistoryOverlay';
import { normalizeDiscogsUrl, extractReleaseId, isMasterRelease } from '../utils/discogsUrlNormalizer';

// Smart API URL switching
const API_BASE_URL =
    window.location.hostname === "staging187611.digassistant.xyz"
        ? "https://api.digassistant.xyz"
        : window.location.hostname === "digassistant.xyz" || window.location.hostname === "www.digassistant.xyz"
            ? "https://api.digassistant.xyz"
            : "http://localhost:8080";

const ServicePage = () => {
    const [url, setUrl] = useState('');
    const [error, setError] = useState('');
    const [urlError, setUrlError] = useState('');
    const [releases, setReleases] = useState([]);
    const [recArtists, setRecArtists] = useState([]);
    const [recTitles, setRecTitles] = useState([]);
    const [videosByReleaseId, setVideosByReleaseId] = useState({});
    const [selectedVideoPopup, setSelectedVideoPopup] = useState(null);

    const [deepCut, setDeepCut] = useState(null); // ⬅️ NEW
    const [startIndex, setStartIndex] = useState(0); // Starting index for sliding window (always show 5)

    const [notFound, setNotFound] = useState(false);
    const [visible, setVisible] = useState(false);
    const [label, setLabel] = useState('');
    const [recommendationLabel, setRecommendationLabel] = useState('');
    const [notFoundMessage, setNotFoundMessage] = useState('');
    const [isRateLimited, setIsRateLimited] = useState(false);
    const [aboutClosed] = useState(true);
    const navigate = useNavigate();
    const [showDiveHistory, setShowDiveHistory] = useState(false);
    const [diveStack, setDiveStack] = useState([]);
    const [shouldShowLowResultsMessage, setShouldShowLowResultsMessage] = useState(false);
    const [consecutiveLowResultsCount, setConsecutiveLowResultsCount] = useState(0);

    // Get consecutive <= 3 results count from sessionStorage
    const getConsecutiveLowResultsCount = () => {
        const count = sessionStorage.getItem('consecutiveLowResultsCount');
        return count ? parseInt(count, 10) : 0;
    };

    const incrementConsecutiveLowResultsCount = () => {
        const currentCount = getConsecutiveLowResultsCount();
        const newCount = currentCount + 1;
        sessionStorage.setItem('consecutiveLowResultsCount', newCount.toString());
        setConsecutiveLowResultsCount(newCount);
    };

    const resetConsecutiveLowResultsCount = () => {
        sessionStorage.removeItem('consecutiveLowResultsCount');
        setConsecutiveLowResultsCount(0);
    };

    useEffect(() => {
        // Initialize consecutive count from sessionStorage on mount
        const count = sessionStorage.getItem('consecutiveLowResultsCount');
        setConsecutiveLowResultsCount(count ? parseInt(count, 10) : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (aboutClosed) {
            const fullLabel = 'Enter discogs url:';
            let currentLabel = '';
            const intervalId = setInterval(() => {
                if (currentLabel.length < fullLabel.length) {
                    currentLabel += fullLabel[currentLabel.length];
                    setLabel(currentLabel);
                } else {
                    clearInterval(intervalId);
                }
            }, 30);
            return () => clearInterval(intervalId);
        }
    }, [aboutClosed]);

    const fetchReleases = async (releaseId) => {
        try {
            // reset
            setReleases([]);
            setRecArtists([]);
            setRecTitles([]);
            setDeepCut(null); // ⬅️ NEW
            setNotFound(false);
            setVisible(false);
            setRecommendationLabel('');
            setNotFoundMessage('');
            setError('');
            setUrlError('');

            const response = await axios.get(`${API_BASE_URL}/recommend/seed/${releaseId}`, { withCredentials: true });

            // Server shape assumed:
            // {
            //   label: "Some label",
            //   recommendations: {
            //     recommendations: [...ids],
            //     recommendations_artist: [...],
            //     recommendations_title: [...],
            //     recommendations_label: [...]
            //   },
            //   deep_cut: { release_id, artist, title, label } | null
            // }
            // (unused here but kept if you want later)
            const { recommendations: recData, deep_cut, videos } = response.data;

            // Defensive: ensure arrays are aligned to min length
            const len = Math.min(
                recData.recommendations?.length ?? 0,
                recData.recommendations_artist?.length ?? 0,
                recData.recommendations_title?.length ?? 0,
                recData.recommendations_label?.length ?? 0
            );

            const recommendations = Array.from({ length: len }, (_, index) => ({
                releaseId: recData.recommendations[index],
                artist: recData.recommendations_artist[index],
                title: recData.recommendations_title[index],
                label: recData.recommendations_label[index],
            }));

            const releaseIds = recommendations.map(({ releaseId }) => releaseId);
            setReleases(releaseIds);
            setRecArtists(recommendations.map(({ artist }) => artist));
            setRecTitles(recommendations.map(({ title }) => title));
            setStartIndex(0); // Reset pagination when new recommendations are fetched
            setUrl('');

            // Show message logic: <= 3 results always show "thin" message
            // If 3 consecutive <= 3 results, show "going in circles" message
            if (releaseIds.length <= 3) {
                // Increment consecutive count and always show message
                incrementConsecutiveLowResultsCount();
                setShouldShowLowResultsMessage(true);
            } else {
                // Results > 3: reset consecutive count, don't show message
                resetConsecutiveLowResultsCount();
                setShouldShowLowResultsMessage(false);
            }

            // Store videos for the searched release if present
            if (videos && videos.videos && videos.videos.length > 0) {
                setVideosByReleaseId(prev => ({
                    ...prev,
                    [releaseId]: videos.videos
                }));
            }

            // Set deep cut if present and well-formed
            if (deep_cut && deep_cut.release_id && deep_cut.title) {
                setDeepCut({
                    releaseId: deep_cut.release_id,
                    artist: deep_cut.artist,
                    title: deep_cut.title,
                    label: deep_cut.label,
                });
            } else {
                setDeepCut(null);
            }

            // Batch fetch all videos in one request
            const allIds = recommendations.map(r => r.releaseId);
            if (deep_cut?.release_id) allIds.push(deep_cut.release_id);
            if (allIds.length > 0) {
                fetch(`${API_BASE_URL}/videos?ids=${allIds.join(',')}`, { credentials: 'include' })
                    .then(r => r.ok ? r.json() : null)
                    .then(data => { if (data?.videos) setVideosByReleaseId(prev => ({ ...prev, ...data.videos })); })
                    .catch(() => {});
            }
        } catch (error) {
            // console.error('Fetch error:', error);

            if (error.response) {
                const status = error.response.status;
                const serverMessage =
                    error.response.data?.error ||
                    error.response.data?.message ||
                    null;

                if (status === 404) {
                    setIsRateLimited(false);
                    setNotFoundMessage(
                        "Dig Assistant covers electronic, reggae, soul, funk, rock & hip hop vinyl<br />and is always expanding, this may be too new or not yet curated :)"
                    );
                } else if (status === 400) {
                    setIsRateLimited(false);
                    setNotFoundMessage(serverMessage || "Invalid release ID. Please check the URL format.");
                } else if (status === 429) {
                    setIsRateLimited(true);
                    setNotFoundMessage("You've reached the edge of the wormhole! Explore more by becoming a member");
                } else if (status === 502) {
                    setIsRateLimited(false);
                    setNotFoundMessage("Internal server error. Please try again later.");
                } else if (serverMessage) {
                    setIsRateLimited(false);
                    setNotFoundMessage(serverMessage);
                } else {
                    setIsRateLimited(false);
                    setNotFoundMessage("An unknown error occurred.");
                }
            } else {
                setIsRateLimited(false);
                setNotFoundMessage("A network error occurred. Please check your connection.");
            }

            setNotFound(true);
            setVisible(true);
        }
    };

    const goBack = () => {
        setDiveStack(prev => {
            const stack = [...prev];
            const previous = stack.pop();
            if (!previous) return prev;
            setReleases(previous.releases);
            setRecArtists(previous.recArtists);
            setRecTitles(previous.recTitles);
            setDeepCut(previous.deepCut);
            setStartIndex(0);
            setVisible(true);
            return stack;
        });
    };

    const fetchReleasesDive = async (releaseId) => {
        try {
            // push current state before navigating
            setDiveStack(prev => [...prev, { releases, recArtists, recTitles, deepCut }]);

            // reset
            setReleases([]);
            setRecArtists([]);
            setRecTitles([]);
            setDeepCut(null);
            setNotFound(false);
            setVisible(false);
            setRecommendationLabel('');
            setNotFoundMessage('');
            setError('');
            setUrlError('');

            const response = await axios.get(`${API_BASE_URL}/recommend/dive/${releaseId}`, { withCredentials: true });

            const { recommendations: recData, deep_cut, videos } = response.data;

            // Defensive: ensure arrays are aligned to min length
            const len = Math.min(
                recData.recommendations?.length ?? 0,
                recData.recommendations_artist?.length ?? 0,
                recData.recommendations_title?.length ?? 0,
                recData.recommendations_label?.length ?? 0
            );

            const recommendations = Array.from({ length: len }, (_, index) => ({
                releaseId: recData.recommendations[index],
                artist: recData.recommendations_artist[index],
                title: recData.recommendations_title[index],
                label: recData.recommendations_label[index],
            }));

            const releaseIds = recommendations.map(({ releaseId }) => releaseId);
            setReleases(releaseIds);
            setRecArtists(recommendations.map(({ artist }) => artist));
            setRecTitles(recommendations.map(({ title }) => title));
            setStartIndex(0); // Reset pagination when new recommendations are fetched
            setUrl('');

            // Show message logic: <= 3 results always show "thin" message
            // If 3 consecutive <= 3 results, show "going in circles" message
            if (releaseIds.length <= 3) {
                // Increment consecutive count and always show message
                incrementConsecutiveLowResultsCount();
                setShouldShowLowResultsMessage(true);
            } else {
                // Results > 3: reset consecutive count, don't show message
                resetConsecutiveLowResultsCount();
                setShouldShowLowResultsMessage(false);
            }

            // Store videos for the searched release if present
            if (videos && videos.videos && videos.videos.length > 0) {
                setVideosByReleaseId(prev => ({
                    ...prev,
                    [releaseId]: videos.videos
                }));
            }

            // Set deep cut if present and well-formed
            if (deep_cut && deep_cut.release_id && deep_cut.title) {
                setDeepCut({
                    releaseId: deep_cut.release_id,
                    artist: deep_cut.artist,
                    title: deep_cut.title,
                    label: deep_cut.label,
                });
            } else {
                setDeepCut(null);
            }

            // Batch fetch all videos in one request
            const allIds = recommendations.map(r => r.releaseId);
            if (deep_cut?.release_id) allIds.push(deep_cut.release_id);
            if (allIds.length > 0) {
                fetch(`${API_BASE_URL}/videos?ids=${allIds.join(',')}`, { credentials: 'include' })
                    .then(r => r.ok ? r.json() : null)
                    .then(data => { if (data?.videos) setVideosByReleaseId(prev => ({ ...prev, ...data.videos })); })
                    .catch(() => {});
            }
        } catch (error) {
            if (error.response) {
                const status = error.response.status;
                const serverMessage =
                    error.response.data?.error ||
                    error.response.data?.message ||
                    null;

                if (status === 404) {
                    setIsRateLimited(false);
                    setNotFoundMessage(
                        "Dig Assistant covers electronic, reggae, soul, funk, rock & hip hop vinyl<br />and is always expanding, this may be too new or not yet curated :)"
                    );
                } else if (status === 400) {
                    setIsRateLimited(false);
                    setNotFoundMessage(serverMessage || "Invalid release ID. Please check the URL format.");
                } else if (status === 429) {
                    setIsRateLimited(true);
                    setNotFoundMessage("You've reached the edge of the wormhole! Explore more by becoming a member");
                } else if (status === 502) {
                    setIsRateLimited(false);
                    setNotFoundMessage("Internal server error. Please try again later.");
                } else if (serverMessage) {
                    setIsRateLimited(false);
                    setNotFoundMessage(serverMessage);
                } else {
                    setIsRateLimited(false);
                    setNotFoundMessage("An unknown error occurred.");
                }
            } else {
                setIsRateLimited(false);
                setNotFoundMessage("A network error occurred. Please check your connection.");
            }

            setNotFound(true);
            setVisible(true);
        }
    };

    useEffect(() => {
        if (releases.length > 0) {
            setVisible(false);
            setTimeout(() => setVisible(true), 1000);
            const fullLabel = 'You might like:';
            let currentLabel = '';
            const intervalId = setInterval(() => {
                if (currentLabel.length < fullLabel.length) {
                    currentLabel += fullLabel[currentLabel.length];
                    setRecommendationLabel(currentLabel);
                } else {
                    clearInterval(intervalId);
                }
            }, 10);
            return () => clearInterval(intervalId);
        }
    }, [releases]);

    useEffect(() => {
        if (notFound || releases.length > 0) {
            setVisible(true);
        } else {
            setVisible(false);
        }
    }, [notFound, releases]);

    const isValidUrl = (url) => {
        // Require discogs.com to be followed by end of string, path separator, query, or fragment
        // This prevents fake domains like discogs.com.fake.com from passing
        const regex = /^(https?:\/\/)?(www\.)?discogs\.com([/?#]|$)/i;
        return regex.test(url);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setUrlError('');
        setError('');
        setNotFound(false);
        setNotFoundMessage('');
        resetConsecutiveLowResultsCount(); // Reset consecutive tracking on new search

        // Clear previous results immediately to prevent flash of old results
        setReleases([]);
        setRecArtists([]);
        setRecTitles([]);
        setDeepCut(null);
        setVisible(false);
        setRecommendationLabel('');

        const trimmedUrl = url.trim();
        setUrl(trimmedUrl); // Update state with trimmed value
        if (isValidUrl(trimmedUrl)) {
            // Normalize URL to strip locale prefixes (e.g., /en/, /fr/, etc.)
            const normalizedUrl = normalizeDiscogsUrl(trimmedUrl);

            // Check if it's a master release
            if (isMasterRelease(normalizedUrl)) {
                // Handle master release: get random release_id from master
                const masterId = extractReleaseId(normalizedUrl);
                if (masterId) {
                    setError('');
                    try {
                        const masterResponse = await axios.get(`${API_BASE_URL}/master/${masterId}`, { withCredentials: true });
                        if (masterResponse.data && masterResponse.data.release_id) {
                            fetchReleases(masterResponse.data.release_id);
                        } else {
                            setError('invalid-url');
                        }
                    } catch (error) {
                        console.error('Error fetching master release:', error);
                        if (error.response) {
                            const status = error.response.status;
                            const serverMessage =
                                error.response.data?.error ||
                                error.response.data?.message ||
                                null;

                            if (status === 404) {
                                setIsRateLimited(false);
                                setNotFoundMessage(
                                    "Dig Assistant covers electronic, reggae, soul, funk, rock & hip hop vinyl<br />and is always expanding, this may be too new or not yet curated :)"
                                );
                                setNotFound(true);
                            } else if (status === 429) {
                                setIsRateLimited(true);
                                setNotFoundMessage("You've reached the edge of the wormhole! Explore more by becoming a member");
                                setNotFound(true);
                            } else if (serverMessage) {
                                setIsRateLimited(false);
                                setNotFoundMessage(serverMessage);
                                setNotFound(true);
                            } else {
                                setError('invalid-url');
                            }
                        } else {
                            setError('invalid-url');
                        }
                    }
                } else {
                    setError('invalid-url');
                }
            } else {
                // Handle regular release
                const releaseId = extractReleaseId(normalizedUrl);
                if (releaseId) {
                    setError('');
                    fetchReleases(releaseId);
                } else {
                    setError('invalid-url');
                }
            }
        } else {
            setUrlError("Please enter a valid Discogs URL :)");
        }
    };

    return (
        <div>
            <FreeDropdown />
            <div className="App">
                <a href="https://www.timehri.world/music" target="_blank" rel="noopener noreferrer">
                    <img src={logo} alt="Logo" className="logo" />
                </a>


                {error && error !== 'invalid-url' && <div className="error-message">{error}</div>}

                {error && error === 'invalid-url' && (
                    <div style={{
                        textAlign: 'center',
                        position: 'absolute',
                        top: window.innerWidth <= 600 ? '22%' : '28%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '100%',
                        maxWidth: '600px',
                        marginBottom: '20px',
                        padding: window.innerWidth <= 600 ? '0 20px' : '0'
                    }}>
                        <div style={{
                            color: '#009e4a',
                            fontFamily: 'CustomFont',
                            fontSize: window.innerWidth <= 600 ? '0.65rem' : '0.75rem',
                            lineHeight: '1.6',
                            marginBottom: '20px',
                            opacity: 0.8
                        }}>
                            <span style={{ whiteSpace: window.innerWidth <= 600 ? 'normal' : 'nowrap' }}>Oops! Dig Assistant works with Discogs vinyl pages.</span><br />
                            Try pasting a link like: https://www.discogs.com/release/2111-UR-The-Final-Frontier
                        </div>
                        <img
                            src="https://images.squarespace-cdn.com/content/v1/5f30083236006c73677947bf/1597164454026-1NTL28QID02MZ7Y71VZD/WORMHOLE_GREEN.png?format=1500w"
                            alt="wormhole"
                            className="floating-wormhole"
                            style={{
                                position: 'relative',
                                width: '150px',
                                height: 'auto',
                                opacity: 0.2
                            }}
                        />
                    </div>
                )}

                <div style={{ position: 'absolute', top: 'calc(40% - 50px)', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'CustomFont', fontSize: '0.7rem', letterSpacing: '0.05em', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span
                            style={{ color: 'rgba(0,158,74,0.25)', cursor: 'not-allowed', position: 'relative' }}
                            onMouseOver={e => {
                                e.currentTarget.style.color = 'rgba(0,158,74,0.5)';
                                e.currentTarget.querySelector('.members-tooltip').style.display = 'block';
                            }}
                            onMouseOut={e => {
                                e.currentTarget.style.color = 'rgba(0,158,74,0.25)';
                                e.currentTarget.querySelector('.members-tooltip').style.display = 'none';
                            }}
                        >voyager<span className="members-tooltip" style={{ display: 'none', position: 'absolute', bottom: '120%', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#111', color: '#009e4a', fontSize: '0.6rem', padding: '3px 8px', borderRadius: '4px', whiteSpace: 'nowrap', pointerEvents: 'none' }}>members only</span></span>
                        <span style={{ color: 'rgba(0,158,74,0.2)' }}>/</span>
                        <span style={{ color: '#009e4a', cursor: 'default' }}>direct</span>
                    </div>
                </div>

                <form onSubmit={handleSubmit} noValidate className="url-form">
                    <span className="label">{label}</span>
                    <input
                        id="url-input"
                        type="url"
                        value={url}
                        onChange={(e) => {
                            setUrl(e.target.value);
                            setError('');
                            setUrlError('');
                        }}
                        required
                    />
                    <button type="submit" className="submit-button">⬆︎</button>
                </form>

                {urlError && (
                    <div style={{
                        textAlign: 'center',
                        position: 'absolute',
                        top: '28%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '100%',
                        maxWidth: '600px',
                        marginBottom: '20px'
                    }}>
                        <div style={{
                            color: '#009e4a',
                            fontFamily: 'CustomFont',
                            fontSize: '0.75rem',
                            lineHeight: '1.6',
                            marginBottom: '20px',
                            opacity: 0.8
                        }}>
                            Please enter a valid Discogs URL, or <span onClick={() => navigate('/upgrade')} style={{ textDecoration: 'underline', cursor: 'pointer' }}>become a member</span> to use voyager mode :)
                        </div>
                        <img
                            src="https://images.squarespace-cdn.com/content/v1/5f30083236006c73677947bf/1597164454026-1NTL28QID02MZ7Y71VZD/WORMHOLE_GREEN.png?format=1500w"
                            alt="wormhole"
                            className="floating-wormhole"
                            style={{
                                position: 'relative',
                                width: '150px',
                                height: 'auto',
                                opacity: 0.2
                            }}
                        />
                    </div>
                )}

                <div className={`recommendations ${visible ? 'visible' : 'hidden'}`}>
                    {notFound ? (
                        <span className="label">
                            {(() => {
                                if (isRateLimited) {
                                    return (
                                        <div style={{ textAlign: 'center', position: 'relative' }}>
                                            <span style={{ display: 'block', marginBottom: '20px' }}>
                                                {notFoundMessage}
                                            </span>
                                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                                                <button
                                                    onClick={() => navigate('/upgrade')}
                                                    style={{
                                                        background: 'none',
                                                        border: '1px solid #009e4a',
                                                        color: '#009e4a',
                                                        padding: '6px 16px',
                                                        borderRadius: '5px',
                                                        cursor: 'pointer',
                                                        fontFamily: 'CustomFont',
                                                        fontSize: '14px',
                                                        transition: 'all 0.2s',
                                                        display: 'block'
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.target.style.background = '#009e4a';
                                                        e.target.style.color = 'black';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.target.style.background = 'none';
                                                        e.target.style.color = '#009e4a';
                                                    }}
                                                >
                                                    Get Unlimited Access
                                                </button>
                                            </div>
                                            <img
                                                src="https://images.squarespace-cdn.com/content/v1/5f30083236006c73677947bf/1597164454026-1NTL28QID02MZ7Y71VZD/WORMHOLE_GREEN.png?format=1500w"
                                                alt="wormhole"
                                                className="floating-wormhole"
                                                style={{
                                                    position: 'relative',
                                                    width: '150px',
                                                    height: 'auto',
                                                    opacity: 0.2,
                                                    display: 'block',
                                                    margin: '0 auto'
                                                }}
                                            />
                                        </div>
                                    );
                                }

                                // For other errors, display message as-is
                                return (
                                    <div style={{ textAlign: 'center', position: 'relative' }}>
                                        <span dangerouslySetInnerHTML={{ __html: notFoundMessage }} style={{ display: 'block', marginBottom: '20px' }} />
                                        <img
                                            src="https://images.squarespace-cdn.com/content/v1/5f30083236006c73677947bf/1597164454026-1NTL28QID02MZ7Y71VZD/WORMHOLE_GREEN.png?format=1500w"
                                            alt="wormhole"
                                            className="floating-wormhole"
                                            style={{
                                                position: 'relative',
                                                width: '150px',
                                                height: 'auto',
                                                opacity: 0.2
                                            }}
                                        />
                                    </div>
                                );
                            })()}
                        </span>
                    ) : (
                        <>
                            {diveStack.length > 0 && (
                                <button
                                    onClick={goBack}
                                    className="submit-button"
                                    type="button"
                                    style={{ display: 'block', margin: '0 auto 12px' }}
                                >
                                    ← back
                                </button>
                            )}
                            <span className="label">{recommendationLabel}</span>

                            {releases.slice(startIndex, startIndex + 5).map((release, index) => {
                                const actualIndex = startIndex + index;
                                const artist = recArtists[actualIndex];
                                const title = recTitles[actualIndex];
                                const videos = videosByReleaseId[release] || [];
                                const hasVideos = videos.length > 0;

                                const handleVideoClick = (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (hasVideos) {
                                        setSelectedVideoPopup({
                                            releaseId: release,
                                            artist,
                                            title,
                                            videos
                                        });
                                    }
                                };

                                return (
                                    <div className="recommendation-container" key={release}>
                                        <span className="recommendation">
                                            <a
                                                href={`https://www.discogs.com/release/${release}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                }}
                                                style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
                                            >
                                                {`${artist} - ${title}`}
                                            </a>
                                            {hasVideos && (
                                                <button
                                                    className="video-indicator-button"
                                                    onClick={handleVideoClick}
                                                >
                                                    <img
                                                        src="https://storage.googleapis.com/tmhimagebucket/youtubelogogreen.svg"
                                                        alt=">>"
                                                    />
                                                </button>
                                            )}
                                        </span>
                                        <button onClick={() => fetchReleasesDive(release)} className="dive-button">
                                            dive
                                        </button>
                                    </div>
                                );
                            })}

                            {/* More recs / Previous buttons */}
                            {releases.length > 0 && (
                                <div style={{ textAlign: 'center', marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
                                    {startIndex > 0 && (
                                        <button
                                            onClick={() => setStartIndex(prev => Math.max(0, prev - 5))}
                                            className="show-more-button"
                                        >
                                            <span className="arrow">←</span>
                                        </button>
                                    )}
                                    {startIndex + 5 < releases.length && (
                                        <button
                                            onClick={() => setStartIndex(prev => prev + 5)}
                                            className="show-more-button"
                                        >
                                            <span>More recs</span>
                                            <span className="arrow">→</span>
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Quiet path message */}
                            {releases.length > 0 && releases.length <= 3 && shouldShowLowResultsMessage && (
                                <div style={{
                                    marginTop: '20px',
                                    padding: '10px',
                                    textAlign: 'center',
                                    color: '#009e4a',
                                    fontSize: '0.9rem',
                                    opacity: 0.8
                                }}>
                                    {consecutiveLowResultsCount >= 3
                                        ? "Going in circles? Try a different URL"
                                        : "This path looks a bit thin, try diving or clicking back and trying another path"
                                    }
                                </div>
                            )}

                            {/* Deep Cut section */}
                            {deepCut && (() => {
                                const deepCutVideos = videosByReleaseId[deepCut.releaseId] || [];
                                const hasDeepCutVideos = deepCutVideos.length > 0;

                                const handleDeepCutVideoClick = (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (hasDeepCutVideos) {
                                        setSelectedVideoPopup({
                                            releaseId: deepCut.releaseId,
                                            artist: deepCut.artist,
                                            title: deepCut.title,
                                            videos: deepCutVideos
                                        });
                                    }
                                };

                                return (
                                    <div className="deepcut-wrap">
                                        <div className="deepcut-title">Deep cut</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span className="deepcut-link">
                                                <a
                                                    href={`https://www.discogs.com/release/${deepCut.releaseId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    className="deepcut-text"
                                                    style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
                                                >
                                                    {deepCut.artist ? `${deepCut.artist} — ${deepCut.title}` : deepCut.title}
                                                </a>
                                                {hasDeepCutVideos && (
                                                    <button
                                                        className="video-indicator-button"
                                                        onClick={handleDeepCutVideoClick}
                                                    >
                                                        <img
                                                            src="/youtubelogogreen.svg"
                                                            alt=">>"
                                                        />
                                                    </button>
                                                )}
                                            </span>
                                            <button onClick={() => fetchReleasesDive(deepCut.releaseId)} className="dive-button">
                                                dive
                                            </button>
                                        </div>
                                        {deepCut.label && <div className="deepcut-label">({deepCut.label})</div>}
                                    </div>
                                );
                            })()}

                            {/* Dive History button */}
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                                <button
                                    onClick={() => setShowDiveHistory(true)}
                                    className="submit-button"
                                    type="button"
                                >
                                    Dive History
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Dive History Overlay */}
            {showDiveHistory && <DiveSessionHistoryOverlay isVisible={showDiveHistory} onClose={() => setShowDiveHistory(false)} />}

            {/* Video Popup */}
            {selectedVideoPopup && (
                <VideoPopup
                    videos={selectedVideoPopup.videos}
                    releaseId={selectedVideoPopup.releaseId}
                    artist={selectedVideoPopup.artist}
                    title={selectedVideoPopup.title}
                    discogsUrl={`https://www.discogs.com/release/${selectedVideoPopup.releaseId}`}
                    onClose={() => setSelectedVideoPopup(null)}
                />
            )}
        </div>
    );
};

export default ServicePage;
